import { Component, h, Host, Prop, State } from '@stencil/core';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../../redux/store';
import { RootState } from '../../../redux/reducers';
import { differenceInMinutes } from 'date-fns';
import { ComponentView } from '../../../types/types';
import { t } from '../../../redux/utils';
import Warning from '../svg-elements/warning';
import AcceptedCircle from '../svg-elements/accepted-circle';

@Component({
  tag: 'rentware-expiration-timer',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareExpirationTimer {
  private intervalId: number;
  @State() timestamp: number; // value is updated by interval callback in order to trigger re-render
  @Prop({ mutable: true }) expiresAt: number;
  @Prop() component: ComponentView = ComponentView.Default;

  unsubscribe!: Unsubscribe;

  connectedCallback() {
    this.intervalId = setInterval(() => {
      if (!this.expiresAt) {
        return;
      }

      this.timestamp = new Date().getTime();
    }, 30 * 1000);
  }

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        expiresAt: state.orderDetails?.expiresAt,
      };
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
    clearInterval(this.intervalId);
  }

  get expiresInMinutes() {
    return differenceInMinutes(new Date(this.expiresAt * 1000), new Date());
  }

  get lessThanMinuteText() {
    if (this.component === ComponentView.Details) {
      return t('cart.cartExpirationInLessThanAMinute');
    }

    return t('cart.cartExpirationInLessThanAMinute');
  }

  get longExpirationText() {
    if (this.component === ComponentView.Details) {
      return t('cart.cartExpiration', { minutes: this.expiresInMinutes });
    } else if (this.component === ComponentView.Checkout) {
      return t('cart.onlyLeft', { minutes: this.expiresInMinutes });
    }

    return t('cart.cartExpiration', { minutes: this.expiresInMinutes });
  }

  get expirationText() {
    if (this.expiresInMinutes === 0) {
      return this.lessThanMinuteText;
    }

    return this.longExpirationText;
  }

  get expirationIcon() {
    if (this.expiresInMinutes === 0) {
      return <Warning />;
    }

    if (this.component === ComponentView.Details) {
      return <AcceptedCircle />;
    } else if (this.component === ComponentView.Checkout) {
      return <Warning />;
    }
  }

  get expirationClass() {
    if (
      this.expiresInMinutes === 0 || //
      this.component === ComponentView.Checkout
    ) {
      return 'RETDContainer RETDDarkBg';
    }

    return 'RETDContainer';
  }

  render() {
    if (!this.expiresAt) {
      return null;
    }

    if (this.component === ComponentView.Details) {
      return (
        <Host class={this.expirationClass}>
          {this.expirationIcon}
          <span>{this.expirationText}</span>
        </Host>
      );
    }

    if (this.component === ComponentView.Checkout) {
      return (
        <Host class={this.expirationClass}>
          {this.expirationIcon}
          <span>{this.expirationText}</span>
        </Host>
      );
    }

    return <Host>{this.expirationText}</Host>;
  }
}
